import { useMergedRef } from '@mantine/hooks';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { controlStyles } from '../styles/controls';
import { colors } from '../theme/colors.stylex';
import { controlMetrics, controlTypography } from '../theme/controls.stylex';
import { setValidInputDimension } from '../utils/validateStyleDimension';
import type { InputLengthStyleProps, TextFieldProps } from './TextField.types';

const styles = stylex.create({
  field: {
    'boxSizing': 'border-box',
    'backgroundColor': stylex.firstThatWorks<string>(colors.fieldBackground, 'inherit'),
    'height': controlMetrics.height,
    'paddingLeft': controlMetrics.paddingInline,
    'paddingRight': controlMetrics.paddingInline,
    'paddingTop': controlMetrics.paddingBlock,
    'paddingBottom': controlMetrics.paddingBlock,
    'borderRadius': controlMetrics.radius,
    'fontFamily': 'inherit',
    'fontWeight': 'inherit',
    'lineHeight': controlTypography.lineHeight,
    'outlineStyle': 'none',
    '::placeholder': { color: colors.placeholder, opacity: 1 },
  },
  small: { fontSize: controlTypography.fontSize },
  large: { fontSize: controlTypography.fontSizeLarge },
  left: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRightWidth: { 'default': 0, ':focus': controlMetrics.borderWidth, ':active': controlMetrics.borderWidth },
  },
  right: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: { 'default': 0, ':focus': controlMetrics.borderWidth, ':active': controlMetrics.borderWidth },
  },
  disabled: { pointerEvents: 'none' },
  error: {
    marginTop: controlMetrics.messageGap,
    fontSize: controlTypography.errorFontSize,
    lineHeight: controlTypography.errorLineHeight,
    color: colors.borderInvalid,
  },
});

/**
 * Textfield.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps & InputLengthStyleProps>((props, ref) => {
  const {
    readonly = false,
    disabled = false,
    onChange,
    onPaste,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    id,
    name,
    size = 'sm',
    value,
    placeholder,
    extraClassNames = '',
    xstyle,
    testId,
    type = 'text',
    inputGroup,
    step,
    showError,
    errorText,
    min,
    max,
    maxLength,
    minLength,
    required = false,
    autoComplete = 'off',
    inputWidth,
    inputHeight,
    autoFocus = false,
    ...tooltipProps
  } = props;

  const internalRef = useRef<HTMLInputElement | null>(null);
  const mergedRef = useMergedRef(ref, internalRef);
  const [cursor, setCursor] = useState<number | null>(null);
  const tooltipData = getQTipData(tooltipProps);
  const initialAutoFocusRef = useRef(autoFocus);

  useEffect(() => {
    if (initialAutoFocusRef.current) {
      setTimeout(() => {
        internalRef.current?.focus();
      }, 0);
    }
  }, []);

  useEffect(() => {
    const input = internalRef.current;
    if (input && type !== 'number' && type !== 'email') input.setSelectionRange(cursor, cursor);
  }, [ref, cursor, value, type]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCursor(e.target.selectionStart);
    onChange && onChange(e);
  };

  const shouldResetNumberStepBase = () => {
    if (type !== 'number') return false;
    if (min === undefined) return true;

    return min % Number(step ?? 1) === 0;
  };

  const stringifyInputValue = (inputValue: string | string[] | number) =>
    Array.isArray(inputValue) ? inputValue.join(',') : String(inputValue);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (shouldResetNumberStepBase()) {
      const input = e.currentTarget;
      const blurValue = input.value;

      input.defaultValue = '';
      input.value =
        input.validity.valid && value !== null && value !== undefined ? stringifyInputValue(value) : blurValue;
    }
    onBlur?.(e);
  };

  // Number inputs are uncontrolled (see valueProp below) — sync external value changes
  // to the DOM here. Skip while focused so we don't clobber the user mid-type.
  useEffect(() => {
    const input = internalRef.current;

    if (type !== 'number') return;
    if (value !== null && value !== undefined && value !== input?.value && input) {
      input.value = stringifyInputValue(value);
    }
  }, [value, type]);

  const appliedStyle = stylex.props(
    controlStyles.base,
    styles.field,
    size === 'lg' ? styles.large : styles.small,
    inputGroup === 'left' && styles.left,
    inputGroup === 'right' && styles.right,
    showError && controlStyles.invalid,
    disabled && controlStyles.disabled,
    disabled && styles.disabled,
    xstyle,
  );
  const dimensions = setValidInputDimension(inputWidth, inputHeight);

  const inputLenghtProp: InputLengthStyleProps = {};
  if (maxLength) inputLenghtProp.maxLength = maxLength;
  if (minLength) inputLenghtProp.minLength = minLength;

  // CRAB-59299: number inputs use `defaultValue` (uncontrolled) so React doesn't keep
  // rewriting the value attribute — that attribute is the browser's step base, and
  // controlled writes caused wrong arrow increments / step-mismatch errors. `handleBlur`
  // clears defaultValue so the step base falls back to `min`/0.
  const valueProp = type === 'number' ? { defaultValue: value } : { value };

  return (
    <>
      <input
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        type={type}
        {...valueProp}
        {...appliedStyle}
        className={clsx('specTextField', appliedStyle.className, extraClassNames)}
        style={{ ...appliedStyle.style, ...dimensions }}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        aria-invalid={showError || undefined}
        autoComplete={autoComplete}
        onChange={handleChange}
        onPaste={onPaste}
        onFocus={onFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        step={step}
        required={required}
        min={min}
        max={max}
        {...inputLenghtProp}
        {...tooltipData}
      />
      {errorText && showError && <div {...stylex.props(styles.error)}>{errorText}</div>}
    </>
  );
});
TextField.displayName = 'TextField';
